import React from 'react';
import type { FormikValues } from 'formik';

import { useStyles } from '~/assets/styles';
import { displayRoleName, getAllSearchableContactRoles } from '~/components/communications/ContactUtils';
import { ContactEntity } from '~/components/Contact';
import ContactTextFieldFormik from '~/components/ContactTextFieldFormik';
import { Text } from '~/components/core';
import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import useOrganization from '~/components/OrganizationContext';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';

interface NonViewCommunicationCardHeaderContactDetailsProps {
  enableChoosingContact?: boolean;
  contactId?: number;
  contactFullName?: string;
  contactRole?: string;
  onSelectContact?: (contact: ContactFullModel) => void;
  getValuesOnSelectContact?: (contact: ContactFullModel | '', valuesWithContact: FormikValues) => FormikValues;
  dynamicContactLabel?: string;
  outOfContactsContextSearch?: boolean;
}

const NonViewCommunicationCardHeaderContactDetails: React.FC<NonViewCommunicationCardHeaderContactDetailsProps> = ({
  enableChoosingContact,
  contactId,
  contactFullName,
  contactRole,
  onSelectContact,
  getValuesOnSelectContact,
  dynamicContactLabel,
  outOfContactsContextSearch,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organizationContactRolesDict } = useOrganization();
  const { classes } = useStyles();

  if (enableChoosingContact) {
    return (
      <div className="w-1/2 max-w-[280px]">
        <ContactTextFieldFormik
          id="contact"
          label={dynamicContactLabel ? dynamicContactLabel : 'Contact'}
          className={classes.textField}
          fullWidth
          style={{ width: '280px' }}
          acceptedRoles={getAllSearchableContactRoles(organizationContactRolesDict).concat(['user'])} // allow communicating with users
          fixedSearchResults
          onSelectContact={onSelectContact}
          getValuesOnSelectContact={getValuesOnSelectContact}
          outOfContactsContextSearch={outOfContactsContextSearch}
        />
      </div>
    );
  }

  // Can happen when we reply / forward and the email is not attached to a specific contact
  if (!contactId) {
    return null;
  }

  return (
    <>
      <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.SEMI_BOLD} className="flex">
        <ContactEntity contactId={contactId} contactDisplayName={contactFullName} />
        <span className="ml-16">
          <em>{displayRoleName(organizationContactRolesDict, contactRole)}</em>
        </span>
      </Text>
      <ErrorHelperTextFormik id="contact" />
    </>
  );
};

export default NonViewCommunicationCardHeaderContactDetails;
